// src/components/landingpage/downloads/downloadsFeed.js
//
// Reads an app's downloads data file (data note v1 §1–§2): one GET on page
// load, public, no token, no headers. It is served as text/plain; the body is
// JSON.
//
// The file is public and editable by whoever holds the repository, so nothing
// in it is trusted further than text: every value is coerced to a string, the
// lists are whatever length arrives, unknown fields are ignored, and the
// download address is only accepted when it is an https URL.
//
// Any failure — network, non-200, a body that is not JSON — resolves to null,
// and the page falls back to its built-in copy and the stable download link.

const cache = {}; // feed url -> Promise<data | null>, one read per page load

const str = (v) => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');
const list = (a) => (Array.isArray(a) ? a : []).map(str).filter(Boolean);
const rows = (a) => (Array.isArray(a) ? a : [])
  .map((r) => ({ key: str(r && r.key), label: str(r && r.label), value: str(r && r.value) }))
  .filter((r) => r.label && r.value);

/** Only an https address is ever put in the Download button. */
export const safeUrl = (u, fallback) => {
  try {
    const x = new URL(String(u));
    return x.protocol === 'https:' ? x.href : fallback;
  } catch { return fallback; }
};

export function normaliseFeed(json, fallbackUrl) {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
  const title = str(json.title);
  const details = rows(json.details);
  // A file with neither a title nor a single detail row is not this file.
  if (!title && details.length === 0) return null;
  const section = (o, itemsKey, map) => ({ heading: str(o && o.heading), [itemsKey]: map(o && o[itemsKey]) });
  return {
    schema: Number(json.schema) || 1,
    version: str(json.version),
    released_at: str(json.released_at),
    title,
    description: str(json.description),
    download_url: safeUrl(json.download_url, fallbackUrl),
    details,
    whats_new: section(json.whats_new, 'items', list),
    install: section(json.install, 'steps', list),
    verify: { ...section(json.verify, 'items', rows), note: str(json.verify && json.verify.note) },
  };
}

export function loadDownload(app) {
  if (cache[app.feed]) return cache[app.feed];
  cache[app.feed] = (async () => {
    try {
      if (typeof fetch !== 'function') return null;
      const res = await fetch(app.feed);
      if (!res || !res.ok) return null;
      return normaliseFeed(await res.json(), app.fallback.download_url);
    } catch {
      return null;
    }
  })();
  return cache[app.feed];
}

/** Tests only. */
export function __resetDownloads() { Object.keys(cache).forEach((k) => delete cache[k]); }
